const triageOrder = ['none_selected', 'none', 'inconclusive', 'standard', 'immediate', 'urgent']

const getHiddenAssessments = (bundle = []) => {
    return new Set(bundle.filter(
        item => item.type === 'assessment' && item.display_hidden).map(item => item.name)
    );
}

export default {
    bundle: (state) => JSON.parse(JSON.stringify(state.bundle)),

    ready: (state, getters, rootState, rootGetters) => {
        return (rootGetters['v4/mode/get']('allwaysPerformAssessment') || !rootGetters['v4/mode/get']('uploadOnAssess') || (rootGetters['v4/mode/get']('uploadOnAssess') && getters['bundle'].length === 0))
    },

    getUniquePlansByCategory: (state) => (category) => {
        const planCategoryItems = state.bundle.filter(item => (
            item.type === 'plan' && item.category === category && item.shown
        ))
        const uniquePlans = []
        planCategoryItems.forEach(plan => {
            const found = uniquePlans.find(p => p.name === plan.name)
            if (!found) {
                // pull out unwanted fields, independent of assessment/severity
                const { assessment, severity, selected, shown, ...rest } = plan
                uniquePlans.push({ ...rest })
            }
        })
        return uniquePlans
    },

    getSelectedTreatments: (state) => () => {
        const planSelectedItems = state.bundle.filter(item => item.type === 'plan' && item.selected)
        const uniqueSelectedItems = [...new Set(planSelectedItems.map(item => item.name))]
        return uniqueSelectedItems.map(planName => state.bundle.find(item => item.type === 'treatment' && item.name === planName))
    },

    loaded: (state) => {
        let res, rej

        if (window.mrx) return true;

        let promise = new Promise((resolve, reject) => {
            res = resolve
            rej = reject
        })

        promise.resolve = res
        promise.reject = rej
        promise.catch((error) => {
            console.error('MRX Not Loaded')
        })
        return promise
    },

    useDeviceMRX: (state, getters, rootState, rootGetters) => {
        if (rootGetters['v4/settings/get']('cdn')) return true
        if (rootGetters['v4/settings/get']('offline')) return true
        return false
    },

    assessments: (state) => (names) => {
        let results = []
        try {
            if (names) {
                results = state.bundle.filter(item => (item.type === 'assessment'
                    && names.includes(item.name)
                    && !item.display_hidden));
            }
            else {
                results = state.bundle.filter(item => (item.type === 'assessment')
                    && !item.display_hidden);
            }
        }
        catch (e) { console.log(e) }
        return JSON.parse(JSON.stringify(results))
    },

    treatments: (state) => (names) => {
        let results = []
        try {
            const hiddenAssessments = getHiddenAssessments(state.bundle);
            if (names) {
                results = state.bundle.filter(item => (item.type === 'treatment'
                    && names.includes(item.name)
                    && !item.display_hidden
                    && !hiddenAssessments.has(item.assessment)));
            }
            else {
                results = state.bundle.filter(item => (item.type === 'treatment')
                    && !item.display_hidden
                    && !hiddenAssessments.has(item.assessment));
            }
        }
        catch (e) { console.log(e) }
        return JSON.parse(JSON.stringify(results))
    },

    plans: (state) => (names) => {
        let results = []
        try {
            const hiddenAssessments = getHiddenAssessments(state.bundle);
            if (names) {
                results = state.bundle.filter(item => (item.type === 'plan'
                    && names.includes(item.name)
                    && !item.display_hidden)
                    && !hiddenAssessments.has(item.assessment));
            }
            else {
                results = state.bundle.filter(item => (item.type === 'plan')
                    && !item.display_hidden
                    && !hiddenAssessments.has(item.assessment));
            }
        }
        catch (e) { console.log(e) }
        return JSON.parse(JSON.stringify(results))
    },

    triage: (state) => {
        const triage = state.bundle.find(item => (item.type === 'triage'))
        return triage ? triage.name : 'none'
    },

    triageSelected: (state) => () => {
        const selectedAssessments = state.bundle.filter(item => {
            return item.type === 'assessment' && item.selected
        })
        let triage = 'none_selected'
        selectedAssessments.forEach(assessment => {
            const triageRec = assessment.triage_recommendation
            const currentTriageOrder = triageOrder.findIndex(x => x === triage)
            const newTriageOrder = triageOrder.findIndex(x => x === triageRec)
            if (newTriageOrder > currentTriageOrder) {
                triage = triageRec
            }
        })
        return triage
    },

    status: (state) => {
        let results = []
        try {
            results = state.bundle.filter(item => (item.type === 'status'))
        }
        catch (e) { console.log(e) }
        return JSON.parse(JSON.stringify(results))
    },

    valid: (state) => {
        let status = state.bundle.find(b => (b.type === 'status' && b.name === 'validity'))
        return status && status.value === 'valid'
    },

    notification: (state) => {
        let notification = state.bundle.find(b => (b.type === 'notification' && b.name === 'mrx'))
        return notification ? notification.value : null
    },

    hasRiskOfCondition: (state) => (name) => {
        const condition = state.bundle.find(item => (item.type === 'assessment'
            && item.name === name));
        if (!condition || condition.display_hidden) {
            return false
        }
        return ['risk_present', 'risk_mild', 'risk_moderate', 'risk_severe'].includes(condition.severity)
    },

    hasTreatmentsToShow: (state) => {
        const hiddenAssessments = getHiddenAssessments(state.bundle);
        const treatments = state.bundle.filter(item => (
            item.type === 'treatment' &&
            !item.display_hidden &&
            !hiddenAssessments.has(item.assessment)
        ));
        const selectedAssessments = state.bundle
            .filter(item => (item.type === 'assessment' && item.selected && !item.display_hidden))
            .map(item => item.name)

        // user did not select any assessments but there are treatments
        if (treatments.length > 0 && selectedAssessments.length === 0) return true

        for (const t of treatments) {
            if (selectedAssessments.includes(t.assessment)) return true
        }
        return false
    },

    hasCarePlansToShow: (state) => () => {
        const hiddenAssessments = getHiddenAssessments(state.bundle);
        const plans = state.bundle.filter(item => (item.type === 'plan'
            && !item.display_hidden
            && !hiddenAssessments.has(item.assessment)));
        // show if anything was selected (just treatments) or if any non-treatments have "shown"
        return plans.some(p => p.selected || (p.shown && p.category !== 'medication'))
    }
}
